/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import {
  ControlProps,
  RankedTester,
  rankWith,
  isStringControl,
  and
} from '@jsonforms/core'

import { useJsonForms, withJsonFormsControlProps } from '@jsonforms/react'
import { useAppSelector } from 'hooks/store'
import { withHandleChangeEmitter } from '../WithCustomHandlerHOC'
import { CustomUISchema } from '../../constants'
import ToolTipOverlayComponent from '../../Components/ToolTipOverlay'
import './styles.scss'

export const Input: React.FC<ControlProps> = (props: ControlProps) => {
  const {
    label,
    handleChange,
    data,
    path,
    id,
    errors,
    schema,
    required,
    visible,
    enabled
  } = props

  const uischema = props.uischema as CustomUISchema
  const placeHolderText =
    'placeHolderText' in uischema ? uischema.placeHolderText : ''

  const toolTipText = 'toolTipText' in uischema ? uischema.toolTipText : ''

  const [showError, setShowError] = useState<boolean>(false)
  const activeCase = useAppSelector((state) => state.activeCase)
  const [controlClass, setControlClass] = useState('form-control')

  const { core } = useJsonForms()

  useEffect(() => {
    ;(required && !data && core?.validationMode === 'ValidateAndShow') ||
    (errors && errors.length && core?.validationMode === 'ValidateAndShow')
      ? setControlClass('form-control is-invalid')
      : setControlClass('form-control')
  }, [errors, data, core?.validationMode])

  const validate = () => {
    required && !data && core?.validationMode !== 'NoValidation'
      ? setShowError(true)
      : setShowError(false)
  }

  useEffect(() => {
    let timeOut: number

    if (path.includes('$case_type') && !data) {
      timeOut = window.setTimeout(() => {
        const pathVal = activeCase.matter?.isJoint ? 'JOINT' : 'SINGLE'
        handleChange(path, pathVal)
      }, 200)
    }

    return () => {
      clearTimeout(timeOut)
    }
  }, [data])

  useEffect(() => {
    if (core?.validationMode !== 'ValidateAndShow') {
      setShowError(false)
      return
    }
    if (core?.validationMode === 'ValidateAndShow' && !data) {
      validate()
    }
  }, [core?.validationMode])

  const renderTextArea = (): JSX.Element => {
    return (
      <div className='form-group'>
        <label htmlFor={id} className='custom-input-label'>
          {label}
        </label>
        {toolTipText?.length > 0 && (
          <ToolTipOverlayComponent
            toolTipText={toolTipText}
            placement='top'
            trigger='click'
          />
        )}
        <textarea
          value={data ?? ''}
          className={controlClass}
          placeholder={placeHolderText}
          id={id}
          onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) => {
            const value = ev.target.value
            handleChange(path, value)
          }}
          onBlur={() => {
            validate()
          }}
          disabled={!enabled}
          data-testid='textarea-input'
        />

        {(!!errors || showError) && (
          <div className='text-danger'>
            <span>{`${schema.title} is required`}</span>
          </div>
        )}
      </div>
    )
  }

  const renderString = (): JSX.Element => {
    return (
      <div className='form-group'>
        <label htmlFor={id} className='custom-input-label'>
          {label}
        </label>
        {toolTipText && toolTipText.length > 0 && (
          <ToolTipOverlayComponent
            toolTipText={toolTipText}
            placement='top'
            trigger='click'
          />
        )}
        <input
          type='text'
          placeholder={placeHolderText}
          value={data ?? ''}
          className={controlClass}
          id={id}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
            const value = ev.target.value
            handleChange(path, value)
          }}
          onBlur={() => {
            validate()
          }}
          data-testid='custom-input'
          disabled={!enabled}
        />
        {(!!errors || showError) && (
          <div className='text-danger'>
            {(uischema as any).requiredErrorLabel && (
              <span>{(uischema as any).requiredErrorLabel}</span>
            )}
            {!(uischema as any).requiredErrorLabel && (
              <span>{`${schema.title} is required`}</span>
            )}
          </div>
        )}
      </div>
    )
  }

  const toRender = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (schema as any).isTextArea ? renderTextArea() : renderString()
  }

  return <>{visible && toRender()}</>
}

export const inputControlTester: RankedTester = rankWith(
  2,
  and(isStringControl)
)

export const InputControl = withJsonFormsControlProps(
  withHandleChangeEmitter(Input)
)
